"use client";

import { useEffect, useRef } from "react";

/*
 * A shared engine for the dot-grid art: a scene paints a tone (0 to 1) into every
 * cell of a fixed grid each frame, and the canvas draws one dot per cell whose
 * area tracks that tone, in the element's text colour.
 */

const MAX_FILL = 0.94;
const SUB = 3;

export class Paint {
  constructor(
    readonly cols: number,
    readonly rows: number,
    readonly tones: Float32Array,
  ) {}

  /** Blend `tone` into every cell by how much of it `inside` covers. */
  fill(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    inside: (x: number, y: number) => boolean,
    tone: number,
  ) {
    const c0 = Math.max(0, Math.floor(x0));
    const c1 = Math.min(this.cols - 1, Math.ceil(x1));
    const r0 = Math.max(0, Math.floor(y0));
    const r1 = Math.min(this.rows - 1, Math.ceil(y1));
    for (let row = r0; row <= r1; row++) {
      for (let col = c0; col <= c1; col++) {
        let n = 0;
        for (let sy = 0; sy < SUB; sy++) {
          for (let sx = 0; sx < SUB; sx++) {
            if (inside(col + (sx + 0.5) / SUB, row + (sy + 0.5) / SUB)) {
              n++;
            }
          }
        }
        if (n > 0) {
          const i = row * this.cols + col;
          const cur = this.tones[i] ?? 0;
          this.tones[i] = cur + (tone - cur) * (n / (SUB * SUB));
        }
      }
    }
  }

  roundRect(
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    tone: number,
  ) {
    this.fill(
      x,
      y,
      x + w,
      y + h,
      (px, py) => {
        if (px < x || py < y || px > x + w || py > y + h) {
          return false;
        }
        const cx = Math.min(Math.max(px, x + r), x + w - r);
        const cy = Math.min(Math.max(py, y + r), y + h - r);
        return (px - cx) ** 2 + (py - cy) ** 2 <= r * r;
      },
      tone,
    );
  }

  /** A rounded box drawn as a rim of `rimTone` around an interior of `fillTone`. */
  box(
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    rimTone: number,
    fillTone: number,
    rim = 1,
  ) {
    this.roundRect(x, y, w, h, r, rimTone);
    this.roundRect(
      x + rim,
      y + rim,
      w - 2 * rim,
      h - 2 * rim,
      Math.max(0, r - rim),
      fillTone,
    );
  }

  disc(cx: number, cy: number, r: number, tone: number) {
    this.fill(
      cx - r,
      cy - r,
      cx + r,
      cy + r,
      (px, py) => (px - cx) ** 2 + (py - cy) ** 2 <= r * r,
      tone,
    );
  }

  /** A stroke with round caps. */
  line(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    width: number,
    tone: number,
  ) {
    const h = width / 2;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len2 = dx * dx + dy * dy || 1;
    this.fill(
      Math.min(x0, x1) - h,
      Math.min(y0, y1) - h,
      Math.max(x0, x1) + h,
      Math.max(y0, y1) + h,
      (px, py) => {
        const t = Math.min(
          1,
          Math.max(0, ((px - x0) * dx + (py - y0) * dy) / len2),
        );
        return Math.hypot(px - (x0 + dx * t), py - (y0 + dy * t)) <= h;
      },
      tone,
    );
  }
}

export interface DotScene {
  cols: number;
  rows: number;
  /** clock time, in ms, of the frame shown under reduced motion */
  still: number;
  /** returns a painter for this mount; state lives in its closure */
  create: () => (paint: Paint, t: number) => void;
}

/** Deterministic pseudo-random numbers, so every load draws the same specks. */
export function seeded(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
}

/** A sparse scatter of tiny dots around the drawing, like spatter on a print. */
export function specks(paint: Paint, seed: number, density = 0.022) {
  const rand = seeded(seed);
  for (let i = 0; i < paint.cols * paint.rows; i++) {
    const r = rand();
    const v = rand();
    if ((paint.tones[i] ?? 0) === 0 && r < density) {
      paint.tones[i] = 0.02 + v * 0.05;
    }
  }
}

export function DotSceneCanvas({
  scene,
  label,
  className,
}: {
  scene: DotScene;
  label: string;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      return;
    }
    const { cols, rows } = scene;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const painter = scene.create();
    const tones = new Float32Array(cols * rows);
    const paint = new Paint(cols, rows, tones);
    let width = 0;
    let clock = reduced ? scene.still : 0;
    let last = 0;
    let drawnAt = -1e9;
    let frame = 0;

    const render = () => {
      if (width === 0) {
        return;
      }
      tones.fill(0);
      painter(paint, clock);
      const pitch = width / cols;
      ctx.clearRect(0, 0, width, (width * rows) / cols);
      ctx.fillStyle = getComputedStyle(canvas).color;
      ctx.beginPath();
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const tone = tones[row * cols + col] ?? 0;
          const r = (pitch * MAX_FILL * Math.sqrt(Math.min(1, tone))) / 2;
          if (tone < 0.015 || r < 0.35) {
            continue;
          }
          const cx = (col + 0.5) * pitch;
          const cy = (row + 0.5) * pitch;
          ctx.moveTo(cx + r, cy);
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
        }
      }
      ctx.fill();
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(((rect.width * rows) / cols) * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      render();
    };

    const tick = (now: number) => {
      // a clock that stops while off screen, so nothing jumps on return
      clock += Math.min(now - (last || now), 50);
      last = now;
      if (clock - drawnAt >= 33) {
        drawnAt = clock;
        render();
      }
      frame = requestAnimationFrame(tick);
    };
    const start = () => {
      if (reduced || frame) {
        return;
      }
      last = 0;
      frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    const visibility = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        start();
      } else {
        stop();
      }
    });
    visibility.observe(canvas);
    resize();

    return () => {
      stop();
      resizeObserver.disconnect();
      visibility.disconnect();
    };
  }, [scene]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={label}
      style={{ aspectRatio: `${scene.cols} / ${scene.rows}` }}
      className={`block select-none ${className ?? ""}`}
    />
  );
}
